/**
 * Admin sidebar feature packs — per club via brand.admin_feature_pack.
 *
 * Packs:
 *   full  — all permission-gated admin items
 *   pilot — Club / People / Groups / Competitions / Templates / Branding / Invite
 *
 * Resolution order:
 *   1. localStorage smarttt_admin_feature_pack (debug override)
 *   2. brand.admin_feature_pack from API
 *   3. REACT_APP_PILOT_ADMIN=1 → default "pilot" (GCP pilot builds)
 *   4. "full"
 *
 * Super Admins still see Access Control; they no longer force "full" when the
 * environment or club pack is pilot (that was hiding the slim nav in testing).
 */

export const ADMIN_FEATURE_PACKS = {
  full: "full",
  pilot: "pilot",
};

/** Nav item keys used by SideBar. */
export const PILOT_NAV_ITEMS = new Set([
  "dashboard",
  "users",
  "consent",
  "club",
  "competitions",
  "groups",
  "session_templates",
  "branding",
  "invite",
  "access_control", // Super Admin only; still allowed under pilot pack
]);

export const PILOT_ADMIN_PATHS = new Set([
  "/Dashboard",
  "/UserManagement",
  "/Consent",
  "/UsersAdd",
  "/EditUser",
  "/InviteUser",
  "/Club",
  "/BrandSettings",
  "/GroupList",
  "/GroupsAdd",
  "/EditGroup",
  "/SessionTemplates",
  "/Competitions",
]);

function normalizePack(raw) {
  const v = String(raw || "")
    .trim()
    .toLowerCase();
  if (v === "pilot" || v === "pilot_core" || v === "1" || v === "true") {
    return ADMIN_FEATURE_PACKS.pilot;
  }
  if (v === "full" || v === "0" || v === "false") {
    return ADMIN_FEATURE_PACKS.full;
  }
  return null;
}

/**
 * @param {{ admin_feature_pack?: string } | null | undefined} brand
 * @returns {"full"|"pilot"}
 */
export function resolveAdminFeaturePack(brand) {
  try {
    if (typeof localStorage !== "undefined") {
      const override = normalizePack(localStorage.getItem("smarttt_admin_feature_pack"));
      if (override) return override;
      // Legacy key
      const legacy = localStorage.getItem("smarttt_pilot_admin");
      if (legacy === "0" || legacy === "false") return ADMIN_FEATURE_PACKS.full;
      if (legacy === "1" || legacy === "true") return ADMIN_FEATURE_PACKS.pilot;
    }
  } catch {
    /* ignore */
  }

  const fromBrand = normalizePack(brand?.admin_feature_pack);
  if (fromBrand) return fromBrand;

  if (String(process.env.REACT_APP_PILOT_ADMIN || "").trim() === "1") {
    return ADMIN_FEATURE_PACKS.pilot;
  }
  return ADMIN_FEATURE_PACKS.full;
}

/** @deprecated Prefer resolveAdminFeaturePack(brand) + canShowAdminNavItem */
export function isPilotAdminMode(user, brand) {
  void user;
  return resolveAdminFeaturePack(brand) === ADMIN_FEATURE_PACKS.pilot;
}

/**
 * @param {string} itemKey
 * @param {{ admin_feature_pack?: string } | null | undefined} brand
 */
export function canShowAdminNavItem(itemKey, brand) {
  const pack = resolveAdminFeaturePack(brand);
  if (pack === ADMIN_FEATURE_PACKS.full) return true;
  return PILOT_NAV_ITEMS.has(itemKey);
}

/** Paths allowed in the admin sidebar during pilot. */
export function isPilotAdminNavPath(path) {
  if (!path) return false;
  if (path === "/AddGroup") return true;
  if (PILOT_ADMIN_PATHS.has(path)) return true;
  return (
    path.startsWith("/EditUser") ||
    path.startsWith("/EditGroup")
  );
}
